import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import './SponsorAd.css';

/**
 * Popup de auspiciantes (modificado del anuncio original — NO es un segundo popup).
 *
 * Flujo:
 *   espera delaySegundos (del sponsor; default 60s) → GET /api/auspiciantes/random
 *   0 → no muestra
 *   1 → ese
 *   2+ → aleatorio del servidor, evita repetir el mismo entre visitas
 *        consecutivas del mismo usuario (hint lastId en localStorage).
 *
 * La selección SIEMPRE viene del backend (active + fechaInicio/fechaFin
 * con hora del servidor). localStorage solo recuerda el último id mostrado.
 *
 * Mantiene el fallback a /api/config (Configuracion.ad*) si el nuevo
 * endpoint falla o devuelve vacío y adActive sigue activo.
 */
export default function SponsorAd() {
  const [show, setShow] = useState(false);
  const [sponsor, setSponsor] = useState(null); // modelo Sponsor vigente
  const [fallbackAd, setFallbackAd] = useState(null); // Configuracion.ad* backward-compat

  useEffect(() => {
    // Solo una vez por sesión (comportamiento actual conservado).
    if (sessionStorage.getItem('ad_shown')) return undefined;

    let cancelled = false;
    let timer = null;

    const lastId = localStorage.getItem('rc_last_sponsor') || '';

    const programarShow = (segundos) => {
      if (cancelled) return;
      const raw = Number(segundos);
      const delayMs = (Number.isFinite(raw) && raw >= 1 ? Math.min(3600, Math.round(raw)) : 60) * 1000;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        setShow(true);
        sessionStorage.setItem('ad_shown', 'true');
      }, delayMs);
    };

    const cargar = async () => {
      try {
        const qs = lastId ? `?lastId=${encodeURIComponent(lastId)}` : '';
        const r = await api.get(`/auspiciantes/random${qs}`);
        if (cancelled) return;

        const s = r.data?.sponsor;
        if (s && s.imageUrl) {
          setSponsor(s);
          programarShow(s.delaySegundos);
        } else {
          // Fallback backward-compat: anuncio global de Configuracion.ad*
          try {
            const cfg = await api.get('/config');
            if (cancelled) return;
            if (cfg.data?.adActive && cfg.data?.adUrl) {
              setFallbackAd({
                adText: cfg.data.adText,
                adUrl: cfg.data.adUrl,
              });
              programarShow(60);
            }
          } catch {
            /* sin fallback */
          }
        }
      } catch {
        // Silencioso: si falla, intentar solo el fallback de config.
        try {
          const cfg = await api.get('/config');
          if (!cancelled && cfg.data?.adActive && cfg.data?.adUrl) {
            setFallbackAd({ adText: cfg.data.adText, adUrl: cfg.data.adUrl });
            programarShow(60);
          }
        } catch {
          /* nada que mostrar */
        }
      }
    };

    cargar();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, []);

  const contenido = sponsor || fallbackAd;
  if (!show || !contenido) return null;

  const esSponsor = Boolean(sponsor);
  const imageUrl = esSponsor ? sponsor.imageUrl : null;
  const mensaje = esSponsor
    ? sponsor.mensajeAporte
    : fallbackAd.adText;
  const href = esSponsor ? sponsor.targetUrl : fallbackAd.adUrl;

  const handleClicImagen = () => {
    if (esSponsor && sponsor?._id) {
      localStorage.setItem('rc_last_sponsor', String(sponsor._id));
    }
    setShow(false);
  };

  return (
    <div className="ad-overlay fade-in">
      <div className="ad-card slide-up">
        <button className="ad-close" onClick={() => setShow(false)} aria-label="Cerrar anuncio">
          ✕
        </button>

        <div className="ad-badge">Patrocinado</div>

        {esSponsor ? (
          <>
            {/* Jerarquía: 1) imagen clickeable 2) mensaje de aporte (texto, NO botón) */}
            <a
              className="ad-image-link"
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleClicImagen}
              aria-label={`Visitar sitio de ${sponsor.nombre}`}
            >
              <img
                className="ad-image"
                src={imageUrl}
                alt={sponsor.nombre || 'Auspiciante'}
              />
            </a>
            {mensaje ? (
              <>
                <p className="ad-nombre">{sponsor.nombre}</p>
                <p className="ad-mensaje">Apoyaron con: {mensaje}</p>
              </>
            ) : (
              <p className="ad-mensaje ad-mensaje-nombre">{sponsor.nombre}</p>
            )}
          </>
        ) : (
          <>
            <div className="ad-icon">💎</div>
            <h2 className="ad-title">{fallbackAd.adText}</h2>
            <p className="ad-desc">
              Gracias al apoyo de nuestros auspiciantes, Red Cimarrona sigue creciendo.
            </p>
            <a
              href={fallbackAd.adUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-v btn-full ad-btn"
              onClick={() => setShow(false)}
            >
              Visitar Sitio Web
            </a>
          </>
        )}

        <div className="ad-footer">Haz click en la imagen para conocer más · Cerrar ✕</div>
      </div>
    </div>
  );
}
